// Theme: dark | light | custom. Persisted in localStorage, applied as a
// `data-theme` attribute on <html>. Dark is the default; light (warm cream) and
// custom are opt-in via the header button, which cycles dark → custom → light.
//
// Every palette token in index.css is a color-mix() between its dark and light
// endpoints, weighted by the CSS variable --mix (0 % = dark, 100 % = light).
// The presets pin --mix; the custom mode reads --mix-custom, which applyMix()
// writes inline on <html> from the settings slider.

export type Theme = "dark" | "light" | "custom";

/** Header button order: each press moves one step, wrapping at the end. */
export const THEME_CYCLE: readonly Theme[] = ["dark", "custom", "light"];

const STORAGE_KEY = "kitchenos.theme";

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === "light" || raw === "custom" ? raw : "dark";
  } catch {
    return "dark";
  }
}

export function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-theme", theme);
}

/** Custom-mode mix, 0 = dark palette … 1 = light palette, as `--mix-custom` on <html>. */
export function applyMix(mix: number): void {
  if (typeof document === "undefined") return;
  const pct = Math.round(Math.min(1, Math.max(0, mix)) * 100);
  document.documentElement.style.setProperty("--mix-custom", `${pct}%`);
}

/** The light weight the palette is currently rendered with, 0..1. */
export function effectiveMix(theme: Theme, customMix: number): number {
  if (theme === "dark") return 0;
  if (theme === "light") return 1;
  return customMix;
}

/** Monochrome accent (pref "mono"): a `data-mono` attribute on <html>, styled in index.css. */
export function applyMono(on: boolean): void {
  if (typeof document === "undefined") return;
  if (on) document.documentElement.setAttribute("data-mono", "on");
  else document.documentElement.removeAttribute("data-mono");
}

/** Touch mode (pref "touch"): a `data-touch` attribute on <html> that scales
 * the size tokens in index.css. "auto" follows the primary pointer. */
export function applyTouch(pref: "auto" | "on" | "off"): void {
  if (typeof document === "undefined") return;
  const on =
    pref === "on" ||
    (pref === "auto" &&
      typeof window !== "undefined" &&
      window.matchMedia("(pointer: coarse)").matches);
  if (on) document.documentElement.setAttribute("data-touch", "on");
  else document.documentElement.removeAttribute("data-touch");
}

export function setStoredTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage unavailable (private window etc.) — theme still applies for the session.
  }
  applyTheme(theme);
}
